<template>
  <div v-if="canReportProblem">
    <!-- Only on pages without the dashboard sidebar; there its Help items open the form.
         Sits below the app's dialogs (z-50) and hides while its own modal is open. -->
    <button
      v-if="!hasSidebar"
      v-show="!isOpen"
      type="button"
      class="bug-report-trigger"
      aria-haspopup="dialog"
      @click="open"
    >
      <ChatBubbleLeftEllipsisIcon class="h-6 w-6 shrink-0" aria-hidden="true" />
      <!-- Icon only; the label fades in beside it on hover / keyboard focus and still names the button. -->
      <span class="bug-report-trigger__label">Report a problem</span>
    </button>

    <Modal
      :modal-open="isOpen"
      header-text="Report a problem"
      :subheader-text="subheaderText"
      :cta-text="ctaText"
      :show-cancel-button="!submitted"
      :show-success-icon="submitted"
      :success-state="submitted"
      :disable-cta="disableCta"
      @closeModal="handleCta"
      @cancelAction="close"
    >
      <p v-if="submitted" class="text-sm text-gray-600">Thanks — your report is with the team.</p>

      <form v-else class="flex flex-col gap-4 text-left" @submit.prevent="submit">
        <div class="flex flex-col items-start">
          <label for="bug-report-severity" class="input--label">What kind of problem?</label>
          <select
            id="bug-report-severity"
            v-model="form.severity"
            class="input--select w-full"
            :disabled="submitting"
          >
            <option v-for="option in severityOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>

        <div class="flex flex-col items-start">
          <label for="bug-report-message" class="input--label">What went wrong?</label>
          <textarea
            id="bug-report-message"
            ref="messageField"
            v-model="form.message"
            rows="5"
            :maxlength="maxMessageLength"
            :disabled="submitting"
            placeholder="What were you doing, and what happened instead?"
            class="input--text w-full resize-y"
          />
        </div>

        <div class="flex flex-col items-start gap-2">
          <label for="bug-report-screenshots" class="input--label">
            Screenshots
            <span class="font-normal text-gray-500">(optional, up to {{ maxScreenshots }})</span>
          </label>
          <input
            id="bug-report-screenshots"
            ref="screenshotInput"
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            multiple
            :disabled="submitting"
            class="block w-full text-sm text-gray-600 file:mr-3 file:rounded-md file:border-0 file:bg-gray-100 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-gray-700 hover:file:bg-gray-200"
            @change="onScreenshotsSelected"
          />
          <ul v-if="form.screenshots.length" class="flex w-full flex-col gap-2">
            <li
              v-for="(file, index) in form.screenshots"
              :key="`${file.name}-${index}`"
              class="flex items-center justify-between gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700"
            >
              <span class="min-w-0 truncate">{{ file.name }}</span>
              <button
                type="button"
                class="shrink-0 text-gray-500 underline hover:text-gray-800"
                :disabled="submitting"
                @click="removeScreenshot(index)"
              >
                Remove
              </button>
            </li>
          </ul>
        </div>

        <div v-if="!isLoggedIn" class="flex flex-col items-start">
          <label for="bug-report-email" class="input--label">
            Your email <span class="font-normal text-gray-500">(optional)</span>
          </label>
          <input
            id="bug-report-email"
            v-model="form.reporterEmail"
            type="email"
            :disabled="submitting"
            placeholder="So we can follow up"
            class="input--text w-full"
          />
        </div>

        <!-- Honeypot: hidden from real users, so anything here means a bot. -->
        <input
          v-model="form.website"
          type="text"
          name="website"
          tabindex="-1"
          autocomplete="off"
          aria-hidden="true"
          class="absolute -left-[9999px]"
        />

        <p v-if="errorMessage" class="text-sm text-red-600">{{ errorMessage }}</p>
      </form>
    </Modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { ChatBubbleLeftEllipsisIcon } from '@heroicons/vue/24/outline'
import { useHelpWidgets } from '@/composables/helpWidgets'
import Modal from '@/components/Modal.vue'
import {
  MAX_SCREENSHOT_BYTES,
  MAX_SCREENSHOTS,
  SEVERITY_OPTIONS,
  submitBugReport,
} from '@/services/bugReports'

// Matches MAX_MESSAGE_LENGTH on the server, so the field stops before the API does.
const MAX_MESSAGE_LENGTH = 5000

export default {
  name: 'BugReportWidget',
  components: {
    Modal,
    ChatBubbleLeftEllipsisIcon,
  },
  setup() {
    // Open state is shared so the sidebar's Help item can open the form too.
    const { bugReportOpen, canReportProblem, hasSidebar } = useHelpWidgets()
    return { isOpen: bugReportOpen, canReportProblem, hasSidebar }
  },
  data() {
    return {
      submitting: false,
      submitted: false,
      errorMessage: '',
      form: this.emptyForm(),
      severityOptions: SEVERITY_OPTIONS,
      maxMessageLength: MAX_MESSAGE_LENGTH,
      maxScreenshots: MAX_SCREENSHOTS,
    }
  },
  computed: {
    ...mapGetters(['isLoggedIn']),
    subheaderText() {
      if (this.submitted) return ''
      return "Tell us what happened and we'll take a look. We'll capture the page you're on automatically."
    },
    ctaText() {
      if (this.submitted) return 'Close'
      return this.submitting ? 'Sending…' : 'Send report'
    },
    disableCta() {
      if (this.submitted) return false
      return this.submitting || !this.form.message.trim()
    },
  },
  watch: {
    isOpen(open) {
      // Start each report fresh, whether opened here or from the sidebar.
      if (!open) return
      this.form = this.emptyForm()
      this.submitted = false
      this.errorMessage = ''
      this.$nextTick(() => {
        if (this.$refs.screenshotInput) this.$refs.screenshotInput.value = ''
        this.$refs.messageField?.focus()
      })
    },
  },
  methods: {
    emptyForm() {
      return { severity: 'major', message: '', reporterEmail: '', website: '', screenshots: [] }
    },
    open() {
      this.isOpen = true
    },
    close() {
      this.isOpen = false
    },
    onScreenshotsSelected(event) {
      const picked = Array.from(event.target.files || [])
      if (!picked.length) return

      const next = [...this.form.screenshots]
      for (const file of picked) {
        if (next.length >= MAX_SCREENSHOTS) {
          this.errorMessage = `Please attach at most ${MAX_SCREENSHOTS} screenshots.`
          break
        }
        if (file.size > MAX_SCREENSHOT_BYTES) {
          this.errorMessage = 'Each screenshot must be 1 MB or smaller.'
          continue
        }
        if (file.type && !file.type.startsWith('image/')) {
          this.errorMessage = 'Screenshots must be image files.'
          continue
        }
        next.push(file)
      }
      this.form.screenshots = next
      // Allow selecting the same file again after remove.
      event.target.value = ''
    },
    removeScreenshot(index) {
      this.form.screenshots = this.form.screenshots.filter((_, i) => i !== index)
    },
    handleCta() {
      if (this.submitted) {
        this.close()
        return
      }
      this.submit()
    },
    async submit() {
      if (this.disableCta) return

      this.submitting = true
      this.errorMessage = ''
      const { ok, error } = await submitBugReport(this.form)
      this.submitting = false

      if (ok) {
        this.submitted = true
      } else {
        this.errorMessage = error
      }
    },
  },
}
</script>

<style scoped>
.bug-report-trigger {
  @apply fixed z-20 flex h-[3.25rem] w-[3.25rem] items-center justify-center rounded-full border
    border-gray-200 bg-white text-sm font-medium text-gray-700 shadow-lg hover:bg-gray-50
    focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2;
  right: 1.25rem;
  bottom: 1.25rem;
  transition: background-color 150ms ease;
}

.bug-report-trigger__label {
  @apply pointer-events-none absolute right-full top-1/2 mr-2 whitespace-nowrap rounded-full border
    border-gray-200 bg-white px-3 py-1.5 opacity-0 shadow-md;
  transform: translate(0.5rem, -50%);
  transition: opacity 200ms ease, transform 200ms ease;
}

.bug-report-trigger:hover .bug-report-trigger__label,
.bug-report-trigger:focus-visible .bug-report-trigger__label {
  @apply opacity-100;
  transform: translate(0, -50%);
}

/* Phones: smaller, so it covers less of the page. */
@media (max-width: 639px) {
  .bug-report-trigger {
    @apply right-3 h-11 w-11;
    bottom: 0.75rem;
  }

  .bug-report-trigger svg {
    @apply h-5 w-5;
  }
}

@media (prefers-reduced-motion: reduce) {
  .bug-report-trigger__label {
    transition: none;
  }
}

@media print {
  .bug-report-trigger {
    display: none;
  }
}
</style>
